import * as React from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import {
  Heading,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Section } from './Section';
import { Tile } from './Tile';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Award,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronDownIcon,
  ChevronLeft,
  ChevronRight,
  ChevronUpIcon,
  CircleCheckIcon,
  Clock,
  ClockIcon,
  ExternalLinkIcon,
  FileIcon,
  HelpCircle,
  Info,
  ListFilterIcon,
  Lock,
  MoreHorizontal,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  CareSVG,
  CircledCheckSVG,
  HomeSVG,
  PencilSVG,
  TimelapseSVG,
} from '../CustomSVGs';
import { NavCalendarSVG } from '../CustomSVGs/NavCalendarSVG';
import * as CareOsIcons from '../PatientProfile/icons';
import { FlashIconSVG } from '../CustomSVGs/FlashIconSVG';
import { TrendingIconUpSVG } from '../CustomSVGs/TrendingIconUpSVG';
import { TrendingIconDownSVG } from '../CustomSVGs/TrendingIconDownSVG';
import { PencilIcon } from '../../icons/PencilIcon';

/**
 * lucide-react icons actually imported somewhere in src/ (grepped for
 * `from 'lucide-react'`). Keep this list in sync the same way
 * `ui/README.md`'s registry tables are kept in sync — update it when a
 * new lucide icon is introduced.
 */
const LUCIDE_ICONS: Array<[string, React.ComponentType<any>]> = [
  ['AlertTriangle', AlertTriangle],
  ['ArrowLeft', ArrowLeft],
  ['ArrowRight', ArrowRight],
  ['Award', Award],
  ['Calendar', Calendar],
  ['Check', Check],
  ['CheckCircle2', CheckCircle2],
  ['ChevronDown', ChevronDown],
  ['ChevronDownIcon', ChevronDownIcon],
  ['ChevronLeft', ChevronLeft],
  ['ChevronRight', ChevronRight],
  ['ChevronUpIcon', ChevronUpIcon],
  ['CircleCheckIcon', CircleCheckIcon],
  ['Clock', Clock],
  ['ClockIcon', ClockIcon],
  ['ExternalLinkIcon', ExternalLinkIcon],
  ['FileIcon', FileIcon],
  ['HelpCircle', HelpCircle],
  ['Info', Info],
  ['ListFilterIcon', ListFilterIcon],
  ['Lock', Lock],
  ['MoreHorizontal', MoreHorizontal],
  ['Plus', Plus],
  ['Trash2', Trash2],
  ['X', X],
];

const CUSTOM_ICONS: Array<[string, React.ComponentType<any>]> = [
  ['CareSVG', CareSVG],
  ['CircledCheckSVG', CircledCheckSVG],
  ['HomeSVG', HomeSVG],
  ['PencilSVG', PencilSVG],
  ['TimelapseSVG', TimelapseSVG],
  ['NavCalendarSVG', NavCalendarSVG],
  ['FlashIconSVG', FlashIconSVG],
  ['TrendingIconUpSVG', TrendingIconUpSVG],
  ['TrendingIconDownSVG', TrendingIconDownSVG],
  ['PencilIcon (src/icons)', PencilIcon],
];


/**
 * The careOS icon set — `PatientProfile/icons.tsx`.
 *
 * A third set, alongside lucide and the brand SVGs, and until now the only one
 * this tab did not show. That is how it stayed invisible: the lucide grid is
 * curated from a grep for `from 'lucide-react'`, which by construction cannot
 * find these.
 *
 * They are not decoration. `LaneBlockCard` alone renders eleven of them, and
 * they carry the careOS status vocabulary — the medal for frequency, the dashed
 * ring for level progress, the triangle for a lapsed streak. Somebody building
 * a careOS surface who only reads this tab would reach for a lucide equivalent
 * and get a different weight and grid.
 *
 * Derived from the module's own exports, not a list. That distinction earned
 * itself immediately: this section first shipped with the 32 names typed out by
 * hand, and the pharmacy-tab work added `ZapIcon` and `FlagPennantIcon` days
 * later — so the grid was two icons stale before anyone had read it, under a
 * comment claiming it could not be. Reading `Object.entries` off the namespace
 * makes the claim true rather than aspirational.
 */
const CAREOS_ICONS: Array<[string, React.ComponentType<any>]> = Object.entries(
  CareOsIcons as Record<string, unknown>
)
  .filter(
    (entry): entry is [string, React.ComponentType<any>] =>
      typeof entry[1] === 'function' && /^[A-Z]/.test(entry[0])
  )
  .sort(([a], [b]) => a.localeCompare(b));

const CareOsIconGrid = () => (
  <SimpleGrid columns={{ base: 3, sm: 4, md: 6, lg: 8 }} gap={3}>
    {CAREOS_ICONS.filter(([, Icon]) => !!Icon).map(([name, Icon]) => (
      <Tile key={name} label={name}>
        <VStack align="center" justifyContent="center" h="40px" overflow="hidden">
          <Icon />
        </VStack>
      </Tile>
    ))}
  </SimpleGrid>
);

const LucideGrid = () => (
  <SimpleGrid columns={{ base: 3, sm: 4, md: 6, lg: 8 }} gap={3}>
    {LUCIDE_ICONS.map(([name, Icon]) => (
      <Tile key={name} label={name}>
        <VStack align="center">
          <Icon size={22} />
        </VStack>
      </Tile>
    ))}
  </SimpleGrid>
);

const CustomIconGrid = () => (
  <SimpleGrid columns={{ base: 3, sm: 4, md: 6, lg: 8 }} gap={3}>
    {CUSTOM_ICONS.map(([name, Icon]) => (
      <Tile key={name} label={name}>
        <VStack
          align="center"
          justifyContent="center"
          h="40px"
          overflow="hidden"
        >
          <Icon />
        </VStack>
      </Tile>
    ))}
  </SimpleGrid>
);

export const IconAudit = () => (
  <Section
    id="icons"
    title="Icons"
    description="lucide-react is the default icon set; reach for a custom SVG only
      for brand-specific marks (nav glyphs, illustrations) that lucide
      doesn't cover."
  >
    <VStack gap={8} align="stretch">
      <VStack gap={2} align="stretch">
        <Heading size="md">careOS set — PatientProfile/icons.tsx</Heading>
        <Text fontSize="xs" color="muted.400">
          The careOS surfaces draw from their own module, not lucide. Rendered
          from that module directly, so it cannot fall behind. LaneBlockCard uses
          eleven of these; the status vocabulary lives here.
        </Text>
        <CareOsIconGrid />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">lucide-react (in use)</Heading>
        <Text fontSize="xs" color="muted.400">
          Manually curated from a grep of "from 'lucide-react'" across src/ —
          update when a new icon is introduced.
        </Text>
        <LucideGrid />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">
          Custom SVGs — src/components/CustomSVGs/ &amp; src/icons/
        </Heading>
        <CustomIconGrid />
      </VStack>
    </VStack>
  </Section>
);
